import { StorageKeys } from "@/common/constants/storageKeys";
import { Lockr } from "@/common/Lockr";
import { WrapUp, WrapUpStopped, WrapUpEndType } from "@/common/models/wrapUp";
import { ServerEvent } from "@/common/notification";
import {
	AppCacheActions,
	AppCacheState,
	CacheKey,
	useAppCacheStore,
} from "@/common/stores/persistingStores/app";
import { notificationListener } from "@/hooks/notification";
import {
	AgentInteractions,
	InteractionDirection,
	InteractionDto,
	InteractionOperations,
	InteractionStatus,
	InternalPresenceDto,
	Queue,
	UserPresenceChanged,
	VoicemailTranscriptionFinished,
	TextSummaryReadyNotification,
	TextSummaryStatus,
} from "@/repository/models";
import {
	fetchInteractionsFn,
	interactionsQueryKey,
	markInteractionRecentlyClosed,
	forgetRecentlyClosedInteraction,
} from "@/repository/queryFns/interactions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { InteractionType } from "@/common/types";
import { openPopup } from "@/common/helpers/popupHelper";
import { useMessage } from "@/common/messages";
import { useAgentStore, AgentState } from "@/common/stores/persistingStores/agentPermissions";
import { useCallback, useEffect, useRef } from "react";
import {
	useWrapUpStore,
	WrapUpState,
	WrapUpActions,
} from "@/common/stores/nonpersistingStores/wrapup";
import { useQueuesStore, QueuesState } from "@/common/stores/nonpersistingStores/queues";
import { useAuth } from "@/hooks/authentication";
import { useInteractionsHandler } from "@/hooks/interactions";
import {
	useCurrentInteractionStore,
	CurrentInteractionState,
	CurrentInteractionActions,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { useIntl } from "react-intl";
import { PresenceState } from "@/common/constants/presence";
import {
	markLastEmailAsSeen,
	markLastMessageAsSeen,
	markLastWhatsAppAsSeen,
	resumeInteraction,
	setHasCC4TeamsTrue,
	ConversationSummaryKey,
} from "@/repository/queryFns";
import { useSummaryGenerationStore } from "@/common/stores/nonpersistingStores/summaryGeneration/summaryGenerationStore";
import {
	NavigationActions,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import {
	useCallSessionStore,
	CallSessionState,
} from "@/common/stores/nonpersistingStores/callSession";
import { useTextMonitoring } from "@/hooks/supervisor";
import { useResponsiveBreakpoints } from "@/hooks/responsive";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import { useEmailInteractionPreview } from "./useEmailInteractionPreview";
import {
	isNewOfferingWhileInWrapUp,
	shouldEndWrapUpForPresenceUpdate,
} from "./shouldEndWrapUpForPresenceUpdate";

export const useInteractions = (
	selectedId?: string,
	setSelectedId?: React.Dispatch<React.SetStateAction<string | undefined>>,
	// The hook is mounted by several components at once; only the always-mounted instance
	// (useNavigationTabsManager) opens generic-interaction popups, so accepting one opens a single popup.
	isGenericPopupOwner: boolean = false
) => {
	const queryKey = [interactionsQueryKey];

	const { show: showMessage } = useMessage();
	const agentState = useAgentStore((state: AgentState) => state);

	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);
	const updateWrapUp = useWrapUpStore(
		(actions: WrapUpActions) => actions.update
	);

	const intl = useIntl();

	const queues = useQueuesStore((state: QueuesState) => state.queues);

	const setToggledTab = useNavigationStore(
		(state: NavigationActions) => state.setToggledTab
	);

	const currentInteractionRef = useRef<any>(undefined);
	const openedGenericPopupsRef = useRef<Set<string>>(new Set());

	// Ids of callback rows we just applied an Added for, kept only long enough to
	// recognise the trailing half of a reject/requeue Added+Removed pair. See the
	// WaitingCallChanged handler for why this window exists.
	const recentCallbackRequeuesRef = useRef<Map<string, number>>(new Map());
	const CALLBACK_REQUEUE_WINDOW_MS = 5000;

	const {
		userId: currentAgentId,
		isLogoutInProgress: isAgentLogoutInProgress,
		isLogged: userIsLoggedIn,
	} = useAuth();

	const { data: interactions } = useQuery<AgentInteractions>({
		queryKey: queryKey,
		queryFn: fetchInteractionsFn,
		refetchOnMount: "always",
		enabled: userIsLoggedIn,
	});

	const queryClient = useQueryClient();
	const interactionHandler = useInteractionsHandler();

	// Reads the freshest interactions cache rather than a captured render closure. SignalR handlers
	// can fire several cache updates within a single render batch (e.g. an inactivity close plus the
	// presence updates it triggers); building on a stale snapshot would silently overwrite a change
	// another handler just applied — such as leaving a closed interaction in the interaction bar.
	const getLatestInteractions = () =>
		queryClient.getQueryData<AgentInteractions>(queryKey);

	const clearSummaryGenerating = useSummaryGenerationStore(
		(state) => state.clearGenerating
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const appStoreActions = useAppCacheStore((state: AppCacheActions) => state);

	const isTextMonitoring = useTextMonitoring();

	const currentInteractionNew = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.new
	);

	const currentInteractionPreview = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.preview
	);

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const currentInteractionActions = useCurrentInteractionStore(
		(actions: CurrentInteractionActions) => actions
	);

	const { hideEmailPreview } = useEmailInteractionPreview();

	const setIsMobileDetailViewMode = useNavigationStore(
		(actions: NavigationActions) => actions.setIsMobileDetailViewMode
	);

	const { isMobile } = useResponsiveBreakpoints();

	// A waiting row is a callback while it sits in the Request/Offering states. The
	// requeue Removed carries a default status, so callback rows are recognised from
	// the record already held in the cache rather than from the event payload.
	const hasCallbackWaitingRow = (
		waiting: InteractionDto[] | undefined,
		id: string
	) =>
		waiting?.some(
			(x) =>
				x.id === id &&
				(x.status === InteractionStatus.CallbackRequest ||
					x.status === InteractionStatus.CallbackOffering)
		) ?? false;

	const handleInteractionAccepted = (interaction: InteractionDto) => {
		const latestInteractions = getLatestInteractions();

		if (!latestInteractions) {
			return;
		}

		// The interaction is active again — drop any close tombstone so fetchInteractionsFn stops
		// filtering it out (e.g. a re-offer/accept shortly after an inactivity close). (CC4ALL-9525)
		forgetRecentlyClosedInteraction(interaction.id);

		var updatedActive = interactionHandler.processInteractionsChange(
			interaction,
			latestInteractions.active
		);

		const isVoicemail = interaction.type === InteractionType.Voicemail;
		// Exclude the accepted interaction's own id: the latest cache may already contain it (added
		// earlier in the same batch by ActiveCallChanged/ActiveMessagingChanged, or by a sibling
		// mount of this hook), and counting it would wrongly report "other" active interactions.
		const hasOtherActiveInteractions =
			(latestInteractions.active?.filter((i) => i.id !== interaction.id)
				.length ?? 0) > 0;

		// For a co-active voicemail, CurrentInteractionId is intentionally withheld:
		// selection is driven instead by AcceptedInteractionId, which the voicemail
		// pickup handler (useVoicemailSessionManager) sets only for agent-initiated
		// pickups. Keep these two spots in sync — setting CurrentInteractionId here
		// would make a co-active voicemail steal focus regardless of that gate.
		if (!isVoicemail || !hasOtherActiveInteractions) {
			appStoreActions.setCache(CacheKey.CurrentInteractionId, interaction.id);
		}

		hideEmailPreview();

		queryClient.setQueryData<AgentInteractions>(queryKey, {
			...latestInteractions,
			active: updatedActive,
		});

		if (isMobile) {
			setIsMobileDetailViewMode(true);
			setToggledTab();
		}
	};

	const updateParkedEmailAtWrapupIfNeeded = (wrapUpData: WrapUp) => {
		let parkedInteraction = findActiveInteraction(wrapUpData.mediaId);

		if (!parkedInteraction) {
			return;
		}

		parkedInteraction = { ...parkedInteraction };

		const isInWrapUp = wrapUpData && wrapUpData.mediaId == parkedInteraction.id;
		const isOnHold = parkedInteraction.isOnHold;

		if (isInWrapUp && isOnHold) {
			parkedInteraction.isOnHold = false;
			parkedInteraction.changeType = InteractionOperations.Updated;
		}

		updateActiveInteractions(parkedInteraction);
	};

	notificationListener(ServerEvent.WrapUpStarted, (wrapUpData: WrapUp) => {
		if (!wrapUp) {
			return;
		}

		updateParkedEmailAtWrapupIfNeeded(wrapUpData);
	});

	notificationListener<InteractionDto>(
		ServerEvent.ActiveCallChanged,
		(interaction: InteractionDto) => {
			if (
				interaction.changeType == InteractionOperations.Removed &&
				wrapUp &&
				wrapUp.mediaId == interaction.id
			) {
				return;
			}

			interaction.status === InteractionStatus.Parked
				? updateParkedInteractions(interaction)
				: updateActiveInteractions(interaction);
		}
	);

	notificationListener<VoicemailTranscriptionFinished>(
		ServerEvent.VoicemailTranscriptionFinished,
		(voicemailInteraction: VoicemailTranscriptionFinished) => {
			// Find the voicemail in the latest cache (not the stale closure) and build an immutable
			// copy from it: mutating the cached object in place, or re-basing on a stale snapshot,
			// would revert fields another handler updated in the same batch — or, if the voicemail
			// isn't in the stale snapshot yet, silently drop the transcription.
			const existing = getLatestInteractions()?.active?.find(
				(i) => i.id === voicemailInteraction.id
			);

			if (!existing) {
				return;
			}

			const interaction: InteractionDto = {
				...existing,
				description: voicemailInteraction.transcription,
				voicemailIsTranscriptionProcessed: true,
				changeType: InteractionOperations.Updated,
			};

			updateActiveInteractions(interaction);

			if (interaction.id === selectedId) {
				updateCurrentInteraction(null, interaction);
			}
		}
	);

	// The close-time AI summary is generated in the background (off the close request). When it finishes,
	// fill the summary popover live and clear the "summary is generating" state for the wrap-up UI.
	notificationListener<TextSummaryReadyNotification>(
		ServerEvent.TextSummaryReady,
		(summaryReady: TextSummaryReadyNotification) => {
			if (
				summaryReady.status === TextSummaryStatus.Generated &&
				summaryReady.summary
			) {
				queryClient.setQueryData(
					[ConversationSummaryKey, summaryReady.sessionId],
					summaryReady.summary
				);
			}

			clearSummaryGenerating(summaryReady.sessionId);
		}
	);

	const updateParkedInteractions = (interaction: InteractionDto) => {
		const interactionQueue = queues.find((x) => x.id === interaction.queueId);
		const isOperatorOnInteractionQueue =
			interactionQueue &&
			interactionQueue.isOperator &&
			interactionQueue.hasParkingRight;

		if (isOperatorOnInteractionQueue) {
			addParkedInteractionToWaiting(interaction);
		}
	};

	const addParkedInteractionToWaiting = (interaction: InteractionDto) => {
		// Derive both lists from the latest cache: updateActiveWaitingInteractions overwrites
		// active/waiting with what we pass, so building them from a stale snapshot would revive an
		// interaction another handler closed in the same render batch (CC4ALL-9525).
		const latestInteractions = getLatestInteractions();

		if (!latestInteractions) {
			return;
		}

		if (latestInteractions.waiting) {
			const parkedInteractionExists = latestInteractions.waiting.some(
				(x) => x.id === interaction.id
			);

			interaction.changeType =
				parkedInteractionExists &&
				interaction.changeType === InteractionOperations.Added
					? InteractionOperations.Updated
					: InteractionOperations.Added;
		}

		const updatedWaiting = interactionHandler.processInteractionsChange(
			interaction,
			latestInteractions.waiting
		);

		interaction.changeType = InteractionOperations.Removed;

		const updatedActive = interactionHandler.processInteractionsChange(
			interaction,
			latestInteractions.active
		);

		updateActiveWaitingInteractions(updatedActive, updatedWaiting);
	};

	const updateActiveInteractions = (interaction: InteractionDto) => {
		const latestInteractions = getLatestInteractions();

		if (!latestInteractions) {
			queryClient.invalidateQueries({ queryKey });

			return;
		}

		const updatedActive = interactionHandler.processInteractionsChange(
			interaction,
			latestInteractions.active
		);

		let updatedWaiting = undefined;

		if (
			interaction.changeType === InteractionOperations.Removed &&
			interaction.status === InteractionStatus.Waiting
		) {
			// A rejected callback's terminate flow also emits this Removed (with a
			// default "Waiting" status) after the requeued entry was already restored
			// in the waiting list. The waiting-list lifecycle of callbacks is owned by
			// WaitingCallChanged, so never remove callback rows from here.
			const isCallbackRow = hasCallbackWaitingRow(
				latestInteractions.waiting,
				interaction.id
			);

			if (!isCallbackRow) {
				updatedWaiting = interactionHandler.processInteractionsChange(
					interaction,
					latestInteractions.waiting
				);
			}
		}

		updateActiveWaitingInteractions(updatedActive, updatedWaiting);
	};

	const updateActiveWaitingInteractions = (
		updatedActive: InteractionDto[] | undefined,
		updatedWaiting: InteractionDto[] | undefined
	) => {
		const latestInteractions = getLatestInteractions();

		if (!latestInteractions) {
			return;
		}

		queryClient.setQueryData<AgentInteractions>(queryKey, {
			...latestInteractions,
			active: updatedActive ?? latestInteractions.active,
			waiting: updatedWaiting ?? latestInteractions.waiting,
		});
	};

	const addParkedWaitingMessagingInteractions = (
		interaction: InteractionDto
	) => {
		const interactionQueue = queues.find((x) => x.id === interaction.queueId);
		const isOperatorOnInteractionQueue =
			interactionQueue &&
			interactionQueue.isOperator &&
			interactionQueue.hasParkingRight;

		if (isOperatorOnInteractionQueue) {
			addWaitingMessagingInteraction(interaction);
		}
	};

	const addWaitingMessagingInteraction = (interaction: InteractionDto) => {
		const latestInteractions = getLatestInteractions();

		if (!latestInteractions) {
			return;
		}

		const updatedWaiting = interactionHandler.processInteractionsChange(
			interaction,
			latestInteractions.waiting
		);
		queryClient.setQueryData<AgentInteractions>(queryKey, {
			...latestInteractions,
			waiting: updatedWaiting,
		});
	};

	notificationListener<InteractionDto>(
		ServerEvent.WaitingMessagingChanged,
		(interaction: InteractionDto) => {
			if (
				interaction.type == InteractionType.Email &&
				interaction.changeType == InteractionOperations.Removed
			) {
				if (currentInteractionPreview?.id == interaction.id) {
					if (interaction.firstAgentId != Lockr.get(StorageKeys.UserId)) {
						showMessage("workspace", {
							intent: "info",
							text: intl.formatMessage({
								id: "ErrorMessage.PreviewInteractionPickedUpByAnotherAgent",
							}),
						});
					}
					hideEmailPreview();
				}
			}
			interaction.status === InteractionStatus.Parked
				? addParkedWaitingMessagingInteractions(interaction)
				: addWaitingMessagingInteraction(interaction);
		}
	);

	const verifyMonitoringEnded = (interaction: InteractionDto) => {
		if (
			interaction.changeType === InteractionOperations.Removed &&
			interaction.isEndedByAgent
		) {
			showMessage("workspace", {
				intent: "error",
				text: intl.formatMessage({
					id: "ErrorMessage.MonitoringEnded",
				}),
			});
		}
	};

	const isEmailOutboundChangeWhileOutboundCallInitInProgress = useCallback(
		(interaction: InteractionDto) =>
			currentInteractionNew &&
			currentInteractionNew.type === InteractionType.Call &&
			interaction.type === InteractionType.Email &&
			interaction.direction === InteractionDirection.Outbound,
		[currentInteractionNew]
	);

	notificationListener<InteractionDto>(
		ServerEvent.ActiveMessagingChanged,
		(interaction: InteractionDto) => {
			verifyMonitoringEnded(interaction);

			if (isEmailOutboundChangeWhileOutboundCallInitInProgress(interaction)) {
				return;
			}

			const latestInteractions = getLatestInteractions();

			if (!latestInteractions) {
				return;
			}

			const updatedActive = interactionHandler.processInteractionsChange(
				interaction,
				latestInteractions.active
			);

			// Tombstone the id on close so a stale GET /interactions (which keeps returning the
			// interaction as active for seconds after this Removed — the inactivity close commits
			// later) can't resurrect it; clear it on re-add. See fetchInteractionsFn (CC4ALL-9525).
			if (interaction.changeType === InteractionOperations.Removed) {
				markInteractionRecentlyClosed(interaction.id);
			} else if (interaction.changeType === InteractionOperations.Added) {
				forgetRecentlyClosedInteraction(interaction.id);
			}

			queryClient.setQueryData<AgentInteractions>(queryKey, {
				...latestInteractions,
				active: updatedActive,
			});

			if (
				interaction.type == InteractionType.Email &&
				interaction.changeType == InteractionOperations.Added
			) {
				appStoreActions.setCache(CacheKey.CurrentInteractionId, interaction.id);
				appStoreActions.removeCaches([CacheKey.AcceptedInteractionId]);
			}
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.MessagingAccepted,
		(interaction) => handleInteractionAccepted(interaction)
	);

	notificationListener<InteractionDto>(
		ServerEvent.SupervisedActiveInteractionChanged,
		(interaction: InteractionDto) => {
			const userId = Lockr.get(StorageKeys.UserId);

			if (interaction.monitoringSupervisorId !== userId) {
				return;
			}

			updateActiveInteractions(interaction);
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.VoicemailPickedUp,
		(interaction) => handleInteractionAccepted(interaction)
	);

	notificationListener(ServerEvent.UserMembershipChanged, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: queryKey });
	});

	notificationListener(ServerEvent.UserMembershipRemoved, (_: Queue) => {
		queryClient.invalidateQueries({ queryKey: queryKey });
	});

	notificationListener<WrapUp>(
		ServerEvent.WrapUpStopped,
		(wrapUpStopped: WrapUpStopped) => {
			const latestInteractions = getLatestInteractions();

			if (!latestInteractions) {
				return;
			}

			markInteractionRecentlyClosed(wrapUpStopped.mediaId);

			const activeInteractionsList = latestInteractions.active?.filter(
				(i) => i.id !== wrapUpStopped.mediaId
			);
			queryClient.setQueryData<AgentInteractions>(queryKey, {
				...latestInteractions,
				active: activeInteractionsList,
			});
		}
	);

	const setHasTeamsClientTrueOnPresenceChange = (
		internalPresenceDto: InternalPresenceDto
	) => {
		if (
			!internalPresenceDto ||
			!internalPresenceDto.presence ||
			internalPresenceDto.presence?.availability === PresenceState.Offline
		) {
			return;
		}

		const userId = Lockr.get(StorageKeys.UserId);

		if (
			internalPresenceDto.presence?.id === userId &&
			internalPresenceDto?.hasClient === false &&
			!isAgentLogoutInProgress
		) {
			setHasCC4TeamsTrue();
		}
	};

	notificationListener(
		ServerEvent.InternalPresenceChanged,
		(internalPresenceDto: InternalPresenceDto) => {
			setHasTeamsClientTrueOnPresenceChange(internalPresenceDto);

			if (
				internalPresenceDto.presence.id != currentAgentId ||
				internalPresenceDto.isChangedByAgent ||
				internalPresenceDto.agentAlreadyInHandling
			) {
				return;
			}

			const wrapUpInfo = appStoreCache[CacheKey.WrapUpInteractionInfo];

			const latestInteractions = getLatestInteractions();

			if (!latestInteractions) {
				return;
			}

			const activeInteractionsList = latestInteractions.active?.filter(
				(i) => i.id !== wrapUpInfo?.id
			);
			queryClient.setQueryData<AgentInteractions>(queryKey, {
				...latestInteractions,
				active: activeInteractionsList,
			});
		}
	);

	notificationListener(
		ServerEvent.UserPresenceChanged,
		(data: UserPresenceChanged) => {
			if (data.userId !== Lockr.get(StorageKeys.UserId)) {
				return;
			}

			const latestInteractions = getLatestInteractions();

			const interaction = latestInteractions?.active?.find(
				(i) => i.id === wrapUp?.mediaId
			);

			if (
				!interaction ||
				!shouldEndWrapUpForPresenceUpdate(
					data,
					wrapUp?.reason,
					agentState.activity,
					wrapUp?.mediaId
				)
			) {
				return;
			}

			if (!latestInteractions) {
				return;
			}

			const updatedActive = interactionHandler.processInteractionsChange(
				{ ...interaction, changeType: InteractionOperations.Removed },
				latestInteractions.active
			);

			queryClient.setQueryData<AgentInteractions>(queryKey, {
				...latestInteractions,
				active: updatedActive,
			});

			if (isNewOfferingWhileInWrapUp(data.activity) && wrapUp !== null) {
				updateWrapUp({
					endWrapUpType: WrapUpEndType.ByOffering,
				} as WrapUp);
			} else {
				updateWrapUp(null);
			}

			appStoreActions.removeCaches([CacheKey.SessionVariableText]);
			appStoreActions.removeCaches([CacheKey.WrapUpInteractionInfo]);
		}
	);

	// Takes the waiting list to classify against so it always matches the list the caller passes to
	// processInteractionsChange — reading a render-closure snapshot here could diverge from the
	// latest cache the WaitingCallChanged handler mutates, corrupting the Added↔Updated correction.
	const modifyParkedInteraction = useCallback(
		(interaction: InteractionDto, waiting: InteractionDto[] | undefined) => {
			if (!waiting) {
				return interaction;
			}

			const parkedInteraction = waiting.find(
				(x) => x.id === interaction.id && x.status == InteractionStatus.Parked
			);
			if (
				parkedInteraction &&
				interaction.status == InteractionStatus.Offering &&
				interaction.changeType !== InteractionOperations.Removed
			) {
				interaction.changeType = InteractionOperations.Updated;
			}

			return interaction;
		},
		[]
	);

	const modifyCallPickUpOfferingInteraction = useCallback(
		(interaction: InteractionDto, waiting: InteractionDto[] | undefined) => {
			if (!waiting) {
				return interaction;
			}

			const currentInteraction = waiting.find(
				(x) => x.id === interaction.id
			);
			if (
				currentInteraction &&
				interaction.status === InteractionStatus.Offering &&
				interaction.changeType === InteractionOperations.Added
			) {
				interaction.changeType = InteractionOperations.Updated;
			}

			return interaction;
		},
		[]
	);

	notificationListener<InteractionDto>(
		ServerEvent.WaitingCallChanged,
		(interaction: InteractionDto) => {
			const latestInteractions = getLatestInteractions();

			if (!latestInteractions) {
				queryClient.invalidateQueries({ queryKey });

				return;
			}

			interaction = modifyParkedInteraction(
				interaction,
				latestInteractions.waiting
			);
			interaction = modifyCallPickUpOfferingInteraction(
				interaction,
				latestInteractions.waiting
			);

			// A rejected callback is requeued server-side as a Removed (old offering)
			// + Added (requeued entry) event pair sharing the same interaction id, with
			// no guaranteed delivery order. When the Added arrives first it is deduped
			// onto the row already holding that id, and the trailing Removed then wrongly
			// empties the list.
			//
			// Record the deduped Added so the trailing Removed can be told apart from an
			// everyday callback Removed (accept/cancel/complete). The Removed payload
			// carries a default status, so the requeue can only be recognised from this
			// short-lived marker — not from the event itself.
			const isRequeuedCallbackAdded =
				interaction.changeType === InteractionOperations.Added &&
				(interaction.status === InteractionStatus.CallbackRequest ||
					interaction.status === InteractionStatus.CallbackOffering) &&
				latestInteractions.waiting?.some((x) => x.id === interaction.id);

			if (isRequeuedCallbackAdded) {
				recentCallbackRequeuesRef.current.set(interaction.id, Date.now());
			}

			// Only the reject/requeue race needs a refetch. Firing on every callback
			// Removed would fan a getinteractions refetch out to every agent holding the
			// row on a WaitingCallChanged broadcast — a thundering herd for events the
			// optimistic cache already handles. Gate on a recent same-id requeue Added.
			const requeuedAt = recentCallbackRequeuesRef.current.get(interaction.id);
			const isCallbackRequeueRemoval =
				interaction.changeType === InteractionOperations.Removed &&
				requeuedAt !== undefined &&
				Date.now() - requeuedAt < CALLBACK_REQUEUE_WINDOW_MS;

			if (
				interaction.changeType === InteractionOperations.Removed &&
				requeuedAt !== undefined
			) {
				recentCallbackRequeuesRef.current.delete(interaction.id);
			}

			const updatedWaiting = interactionHandler.processInteractionsChange(
				interaction,
				latestInteractions.waiting
			);

			queryClient.setQueryData<AgentInteractions>(queryKey, {
				...latestInteractions,
				waiting: updatedWaiting,
			});

			if (isCallbackRequeueRemoval) {
				// Refetch so the list converges to the server state regardless of the
				// order in which the Removed/Added pair was delivered.
				queryClient.invalidateQueries({ queryKey });
			}
		}
	);

	const updateActiveInteractionsCount = useNavigationStore(
		(actions: NavigationActions) => actions.updateActiveInteractionsCount
	);

	useEffect(() => {
		if (!interactions) {
			return;
		}

		manageMessagingActiveVariables();
		updateCurrentInteraction();

		if (
			interactions?.active?.length !== undefined &&
			interactions?.active?.length > 1
		) {
			appStoreActions.removeCaches([CacheKey.PreviousTab]);
		}

		updateActiveInteractionsCount(interactions?.active?.length ?? 0);
	}, [interactions]);

	const manageMessagingActiveVariables = () => {
		if (!interactions?.active) {
			return;
		}

		appStoreActions.setCache(
			CacheKey.MessagingVariableText,
			interactions?.active?.map((interaction) => {
				const messagingVariableTextArray =
					appStoreCache[CacheKey.MessagingVariableText];

				const cacheValue = messagingVariableTextArray
					? messagingVariableTextArray.find(
							(x: any) => x.key === interaction.id
						)
					: undefined;

				const value =
					cacheValue && !interaction.activeVariableText
						? cacheValue?.value
						: interaction.activeVariableText ?? "";

				return { key: interaction.id, value: value };
			})
		);
	};

	const markLastMessageInConversationAsSeen = useCallback(
		(activeInteraction: InteractionDto) => {
			if (!interactions) {
				return;
			}

			const markLastMessageAsSeenFn = getMarkMessageAsSeenFn(
				activeInteraction.type
			);

			markLastMessageAsSeenFn(activeInteraction.id).then(() => {
				// Resolves asynchronously: processUpdatedInteraction never re-adds a missing item, so
				// an interaction closed while this call was in flight won't be resurrected in the bar.
				const latestInteractions = getLatestInteractions();

				if (!latestInteractions) {
					return;
				}

				const updatedActive = interactionHandler.processInteractionsChange(
					{
						...activeInteraction,
						isLastMessageSeen: true,
						changeType: InteractionOperations.Updated,
					},
					latestInteractions.active
				);

				queryClient.setQueryData<AgentInteractions>(queryKey, {
					...latestInteractions,
					active: updatedActive,
				});
			});
		},
		[interactions]
	);

	const getStartTime = useCallback(
		(activeInteraction: InteractionDto) => {
			const userId = Lockr.get(StorageKeys.UserId);

			if (
				activeInteraction.id === callSession?.id &&
				callSession?.supervisorId === userId
			) {
				return callSession?.supervisingStartDate;
			}

			return activeInteraction.startDate;
		},
		[callSession]
	);

	const findActiveCallInteraction = useCallback(
		(currentInteractionId: string) => {
			if (!interactions?.active) {
				return undefined;
			}

			const activeInteraction = interactions.active.find(
				(i) => i.id === currentInteractionId && i.type === InteractionType.Call
			);

			if (
				activeInteraction &&
				callSession?.state !== AgentInteractionStatus.Offering
			) {
				appStoreActions.removeCaches([CacheKey.OfferingCallId]);
			}

			return activeInteraction;
		},
		[interactions]
	);

	const findActiveInteraction = useCallback(
		(currentInteractionId: string) => {
			if (!interactions?.active) {
				return undefined;
			}

			const activeInteraction = interactions.active.find(
				(i) => i.id === currentInteractionId
			);

			if (currentInteractionNew != undefined && !currentInteractionId) {
				return undefined;
			}

			return activeInteraction || interactions.active[0];
		},
		[interactions, currentInteractionNew]
	);

	const getMarkMessageAsSeenFn = useCallback((type: InteractionType) => {
		switch (type) {
			case InteractionType.Email:
				return markLastEmailAsSeen;
			case InteractionType.WhatsApp:
				return markLastWhatsAppAsSeen;
			case InteractionType.Webchat:
				return markLastMessageAsSeen;
			default:
				return () => Promise.resolve();
		}
	}, []);

	const getActiveInteraction = useCallback(
		(selectedInteraction?: InteractionDto, currentInteractionId?: any) => {
			const offeringCallSessionId = appStoreCache[CacheKey.OfferingCallId];
			const acceptedInteractionId =
				appStoreCache[CacheKey.AcceptedInteractionId];
			let activeInteraction: InteractionDto | undefined;

			if (offeringCallSessionId) {
				activeInteraction = findActiveCallInteraction(offeringCallSessionId);
			}

			activeInteraction =
				selectedInteraction ||
				activeInteraction ||
				findActiveInteraction(acceptedInteractionId ?? currentInteractionId);
			return activeInteraction;
		},
		[findActiveInteraction, findActiveCallInteraction, appStoreCache]
	);

	const updateCurrentInteraction = useCallback(
		(
			_event?: any,
			selectedInteraction?: InteractionDto,
			triggeredByClick: boolean = false
		) => {
			if (!interactions?.active) {
				return;
			}

			if (selectedInteraction) {
				appStoreActions.removeCaches([CacheKey.AcceptedInteractionId]);
				hideEmailPreview();
			}

			if (!selectedInteraction) {
				const previewInteractionId =
					appStoreCache[CacheKey.PreviewInteractionId];

				if (previewInteractionId) {
					var waitingInteraction = interactions.waiting?.find(
						(x) => x.id == previewInteractionId
					);

					if (waitingInteraction) {
						currentInteractionActions.showPreview({
							...waitingInteraction,
							isPickupEnabled:
								queues.find((x) => x.id == waitingInteraction?.queueId)
									?.isPickupEnabled ?? false,
						});
					} else {
						hideEmailPreview();
					}
				}
			}

			const currentInteractionId = appStoreCache[CacheKey.CurrentInteractionId];

			const activeInteraction = getActiveInteraction(
				selectedInteraction,
				currentInteractionId
			);

			if (!activeInteraction) {
				currentInteractionActions.hideActive();

				return;
			}

			if (
				!triggeredByClick &&
				!selectedInteraction &&
				activeInteraction.type === InteractionType.Voicemail &&
				currentInteractionRef.current &&
				activeInteraction.id !== currentInteractionRef.current
			) {
				const currentlyShownInteraction = interactions.active.find(
					(i) => i.id === currentInteractionRef.current
				);
				if (currentlyShownInteraction) {
					const acceptedId = appStoreCache[CacheKey.AcceptedInteractionId];
					// When the voicemail is the one the agent just picked up, let it
					// take focus (fall through to showActive) instead of suppressing
					// it. Otherwise keep the currently shown interaction open.
					if (acceptedId !== activeInteraction.id) {
						return;
					}
					// The accepted id is a one-shot selection trigger; consume it now
					// that it has driven this switch so a later auto-update doesn't
					// re-steal focus from an interaction the agent moved on to.
					appStoreActions.removeCaches([CacheKey.AcceptedInteractionId]);
				}
			}

			if (
				triggeredByClick &&
				activeInteraction.isOnHold &&
				currentInteractionId !== activeInteraction.id &&
				!isTextMonitoring
			) {
				activeInteraction.isOnHold = false;
				resumeInteraction(activeInteraction.id);
			}

			const activeInteractionChanged =
				currentInteractionRef.current &&
				currentInteractionRef.current != activeInteraction.id;

			currentInteractionActions.showActive(
				{
					id: activeInteraction.id,
					type: activeInteraction.type,
					contactInformation: activeInteraction.contactInformation,
					description: activeInteraction.description,
					name: activeInteraction.name,
					queueName: activeInteraction.queueName,
					startDate: getStartTime(activeInteraction),
					firstAgentAssignedAt: activeInteraction.firstAgentAssignedAt,
					queueId: activeInteraction.queueId,
					firstAgentId: activeInteraction.firstAgentId,
					voicemailFilePath: activeInteraction.voicemailFilePath,
					voicemailIsTranscriptionProcessed:
						activeInteraction.voicemailIsTranscriptionProcessed,
					voicemailExpireDate: activeInteraction.voicemailExpireDate,
					entryPointEmail: activeInteraction.entryPointEmail,
					direction: activeInteraction.direction,
					monitoringSupervisorId: activeInteraction.monitoringSupervisorId,
					isOnHold: activeInteraction.isOnHold,
					sendType: queues.find((x) => x.id == activeInteraction.queueId)
						?.emailSendType,
					activeVariableText: activeInteraction.activeVariableText,
					activeVariableTextIsAiGenerated: activeInteraction.activeVariableTextIsAiGenerated,
					offeringVariableText: activeInteraction.offeringVariableText,
					offeringVariableTextIsAiGenerated: activeInteraction.offeringVariableTextIsAiGenerated,
					wasTransferred: activeInteraction.wasTransferred,
					sessionId: activeInteraction.sessionId,
					isUnify: activeInteraction.isUnify,
					agentCallingEnabled: activeInteraction.agentCallingEnabled,
				},
				activeInteractionChanged
			);

			currentInteractionRef.current = activeInteraction.id;

			setSelectedId?.(activeInteraction.id);

			appStoreActions.setCache(
				CacheKey.CurrentInteractionId,
				activeInteraction.id
			);

			if (isGenericPopupOwner) {
				const activeInteractionIds = new Set(
					interactions.active.map((interaction) => interaction.id)
				);
				openedGenericPopupsRef.current.forEach((id) => {
					if (!activeInteractionIds.has(id)) {
						openedGenericPopupsRef.current.delete(id);
					}
				});

				if (
					activeInteraction?.type === InteractionType.Generic &&
					activeInteractionChanged &&
					!openedGenericPopupsRef.current.has(activeInteraction.id)
				) {
					openPopup(activeInteraction.externalUrl, activeInteraction.id);
					openedGenericPopupsRef.current.add(activeInteraction.id);
				}
			}

			if (
				(activeInteraction?.type === InteractionType.Email ||
					activeInteraction?.type === InteractionType.WhatsApp ||
					activeInteraction?.type === InteractionType.Webchat) &&
				!activeInteraction.isLastMessageSeen
			) {
				markLastMessageInConversationAsSeen(activeInteraction);
			}
		},
		[
			findActiveInteraction,
			findActiveCallInteraction,
			markLastMessageInConversationAsSeen,
			getStartTime,
			callSession,
			appStoreCache,
			isGenericPopupOwner,
		]
	);

	return { selectedId, interactions, updateCurrentInteraction };
};
